<script>
import axios from "axios";

export default {
  name: "ResetPasswordView",
  data() {
    return {
      token: "",
      password: "",
      confirmPassword: "",
      showPassword: false,
      showConfirmation: false,
      loading: false,
      completed: false,
      errorMessage: "",
    };
  },
  created() {
    const fragment = new URLSearchParams(window.location.hash.slice(1));
    this.token = fragment.get("token") || "";
    if (window.location.hash) {
      window.history.replaceState(
        window.history.state,
        document.title,
        `${window.location.pathname}${window.location.search}`
      );
    }
    if (!this.token) {
      this.errorMessage = "This password reset link is invalid or incomplete.";
    }
  },
  methods: {
    async submitReset() {
      this.errorMessage = "";
      if (!this.token) {
        this.errorMessage =
          "This password reset link is invalid or incomplete.";
        return;
      }
      if (this.password !== this.confirmPassword) {
        this.errorMessage = "Password and password confirmation do not match.";
        return;
      }
      if (this.password.length < 8) {
        this.errorMessage = "Password must be at least 8 characters long.";
        return;
      }

      this.loading = true;
      try {
        await axios.post(
          `${process.env.VUE_APP_API_BASE_URL}/auth/reset-password`,
          {
            token: this.token,
            password: this.password,
            confirmPassword: this.confirmPassword,
          },
          { headers: { "Content-Type": "application/json" } }
        );
        this.password = "";
        this.confirmPassword = "";
        this.token = "";
        this.completed = true;
      } catch (error) {
        this.errorMessage =
          error.response?.data?.message ||
          "Your password could not be reset. Request a new link and try again.";
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<template>
  <div class="container-fluid bg-body-tertiary">
    <div class="d-flex align-items-center py-4 min-vh-100">
      <main class="recovery-card w-100 m-auto">
        <img
          src="@/assets/logo.png"
          class="mb-4"
          alt="First City Polytechnic"
          width="96.26"
          height="70"
        />

        <template v-if="completed">
          <div class="recovery-icon mx-auto mb-3">
            <i class="bi bi-shield-check"></i>
          </div>
          <h1 class="h3 mb-3 fw-bold">Password updated</h1>
          <p class="text-body-secondary mb-4">
            Your password has been changed and previous portal sessions have
            been signed out.
          </p>
          <div class="d-grid">
            <RouterLink class="btn btn-success" :to="{ name: 'login' }">
              Continue to sign in
            </RouterLink>
          </div>
        </template>

        <template v-else>
          <h1 class="h3 mb-3 fw-bold">Create a new password</h1>
          <p class="text-body-secondary mb-4">
            Use a password you have not used for this account before.
          </p>

          <div v-if="errorMessage" class="alert alert-danger" role="alert">
            <i class="bi bi-exclamation-triangle-fill me-2"></i>
            {{ errorMessage }}
          </div>

          <form @submit.prevent="submitReset">
            <div class="input-group mb-3">
              <div class="form-floating">
                <input
                  id="newPassword"
                  v-model="password"
                  :type="showPassword ? 'text' : 'password'"
                  class="form-control border-end-0"
                  placeholder="New password"
                  autocomplete="new-password"
                  minlength="8"
                  required
                  :disabled="loading || !token"
                />
                <label for="newPassword">New password</label>
              </div>
              <button
                class="input-group-text bg-white border-start-0"
                type="button"
                :aria-label="showPassword ? 'Hide password' : 'Show password'"
                @click="showPassword = !showPassword"
              >
                <i :class="showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
              </button>
            </div>

            <div class="input-group mb-2">
              <div class="form-floating">
                <input
                  id="confirmNewPassword"
                  v-model="confirmPassword"
                  :type="showConfirmation ? 'text' : 'password'"
                  class="form-control border-end-0"
                  placeholder="Confirm password"
                  autocomplete="new-password"
                  minlength="8"
                  required
                  :disabled="loading || !token"
                />
                <label for="confirmNewPassword">Confirm password</label>
              </div>
              <button
                class="input-group-text bg-white border-start-0"
                type="button"
                :aria-label="
                  showConfirmation ? 'Hide password' : 'Show password'
                "
                @click="showConfirmation = !showConfirmation"
              >
                <i
                  :class="showConfirmation ? 'bi bi-eye-slash' : 'bi bi-eye'"
                ></i>
              </button>
            </div>

            <p class="small text-body-secondary mb-4">
              Use at least 8 characters. Do not use your name, email address or
              phone number as your password.
            </p>

            <div class="d-grid gap-2">
              <button
                class="btn btn-success"
                type="submit"
                :disabled="loading || !token"
              >
                <span
                  v-if="loading"
                  class="spinner-border spinner-border-sm me-2"
                  aria-hidden="true"
                ></span>
                {{ loading ? "Updating..." : "Reset password" }}
              </button>
              <RouterLink
                class="btn btn-link text-decoration-none"
                :to="{ name: 'forgotPassword' }"
              >
                Request a new reset link
              </RouterLink>
            </div>
          </form>
        </template>

        <hr class="mt-5" />
        <p class="mt-3 mb-0 text-body-secondary">
          © 2022–{{ new Date().getFullYear() }}
        </p>
      </main>
    </div>
  </div>
</template>

<style scoped>
.recovery-card {
  max-width: 530px;
  padding: 1rem;
}

.recovery-icon {
  align-items: center;
  background: #e9f7ef;
  border-radius: 50%;
  color: #198754;
  display: flex;
  font-size: 2rem;
  height: 72px;
  justify-content: center;
  width: 72px;
}
</style>
